/**
 * Converts numeric amounts into accurate, natural Tamil currency words with "ரூபாய் மட்டும்".
 * Follows the Indian numbering system (Units, Tens, Hundreds, Thousands, Lakhs, Crores).
 */

const UNITS: Record<number, string> = {
  0: 'பூஜ்ஜியம்',
  1: 'ஒன்று',
  2: 'இரண்டு',
  3: 'மூன்று',
  4: 'நான்கு',
  5: 'ஐந்து',
  6: 'ஆறு',
  7: 'ஏழு',
  8: 'எட்டு',
  9: 'ஒன்பது',
};

const TEENS: Record<number, string> = {
  10: 'பத்து',
  11: 'பதினொன்று',
  12: 'பன்னிரண்டு',
  13: 'பதின்மூன்று',
  14: 'பதினான்கு',
  15: 'பதினைந்து',
  16: 'பதினாறு',
  17: 'பதினேழு',
  18: 'பதினெட்டு',
  19: 'பத்தொன்பது',
};

const TENS_EXACT: Record<number, string> = {
  20: 'இருபது',
  30: 'முப்பது',
  40: 'நாற்பது',
  50: 'ஐம்பது',
  60: 'அறுபது',
  70: 'எழுபது',
  80: 'எண்பது',
  90: 'தொண்ணூறு',
};

const TENS_PREFIX: Record<number, string> = {
  20: 'இருபத்து ',
  30: 'முப்பத்து ',
  40: 'நாற்பத்து ',
  50: 'ஐம்பத்து ',
  60: 'அறுபத்து ',
  70: 'எழுபத்து ',
  80: 'எண்பத்து ',
  90: 'தொண்ணூற்று ',
};

const HUNDREDS_EXACT: Record<number, string> = {
  100: 'நூறு',
  200: 'இருநூறு',
  300: 'முந்நூறு',
  400: 'நானூறு',
  500: 'ஐந்நூறு',
  600: 'அறுநூறு',
  700: 'எழுநூறு',
  800: 'எண்ணூறு',
  900: 'தொள்ளாயிரம்',
};

const HUNDREDS_PREFIX: Record<number, string> = {
  100: 'நூற்று ',
  200: 'இருநூற்று ',
  300: 'முந்நூற்று ',
  400: 'நானூற்று ',
  500: 'ஐந்நூற்று ',
  600: 'அறுநூற்று ',
  700: 'எழுநூற்று ',
  800: 'எண்ணூற்று ',
  900: 'தொள்ளாயிரத்து ',
};

const THOUSANDS_EXACT: Record<number, string> = {
  1: 'ஆயிரம்',
  2: 'இரண்டாயிரம்',
  3: 'மூன்றாயிரம்',
  4: 'நான்காயிரம்',
  5: 'ஐயாயிரம்',
  6: 'ஆறாயிரம்',
  7: 'ஏழாயிரம்',
  8: 'எட்டாயிரம்',
  9: 'ஒன்பதாயிரம்',
  10: 'பத்தாயிரம்',
};

function convertLessThan100(n: number): string {
  if (n <= 0) return '';
  if (n < 10) return UNITS[n];
  if (n < 20) return TEENS[n];

  const tens = Math.floor(n / 10) * 10;
  const rem = n % 10;

  if (rem === 0) {
    return TENS_EXACT[tens] || '';
  }
  return (TENS_PREFIX[tens] || '') + UNITS[rem];
}

function convertLessThan1000(n: number): string {
  if (n <= 0) return '';
  if (n < 100) return convertLessThan100(n);

  const h = Math.floor(n / 100) * 100;
  const rem = n % 100;

  if (rem === 0) {
    return HUNDREDS_EXACT[h] || '';
  }
  return (HUNDREDS_PREFIX[h] || '') + convertLessThan100(rem);
}

function convertThousands(th: number, rem: number): string {
  if (th <= 0) return convertLessThan1000(rem);

  if (rem === 0) {
    if (THOUSANDS_EXACT[th]) return THOUSANDS_EXACT[th];
    return convertLessThan100(th) + ' ஆயிரம்';
  }

  let prefix = '';
  if (th === 1) {
    prefix = 'ஆயிரத்து ';
  } else if (THOUSANDS_EXACT[th]) {
    prefix = THOUSANDS_EXACT[th].replace(/ம்$/, 'த்து ');
  } else {
    prefix = convertLessThan100(th) + ' ஆயிரத்து ';
  }

  return prefix + convertLessThan1000(rem);
}

function convertInteger(n: number): string {
  if (n === 0) return UNITS[0];

  const crores = Math.floor(n / 10000000);
  n %= 10000000;

  const lakhs = Math.floor(n / 100000);
  n %= 100000;

  const thousands = Math.floor(n / 1000);
  const remainder = n % 1000;

  let result = '';

  if (crores > 0) {
    const crText = crores === 1 ? 'ஒரு' : convertInteger(crores);
    const hasRemainder = lakhs > 0 || thousands > 0 || remainder > 0;
    result += crText + (hasRemainder ? ' கோடியே ' : ' கோடி ');
  }

  if (lakhs > 0) {
    const lkText = lakhs === 1 ? 'ஒரு' : convertLessThan100(lakhs);
    const hasRemainder = thousands > 0 || remainder > 0;
    result += lkText + (hasRemainder ? ' இலட்சத்து ' : ' இலட்சம் ');
  }

  if (thousands > 0 || remainder > 0) {
    result += convertThousands(thousands, remainder);
  }

  return result.trim().replace(/\s+/g, ' ');
}

export function tamilNumberToWords(amount: number | string | null | undefined): string {
  if (amount === null || amount === undefined || amount === '') {
    return '';
  }

  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return '';

  if (num === 0) {
    return 'பூஜ்ஜியம் ரூபாய் மட்டும்';
  }

  const isNegative = num < 0;
  const absNum = Math.abs(num);

  const integerPart = Math.floor(absNum);
  const decimalPart = Math.round((absNum - integerPart) * 100);

  let words = convertInteger(integerPart);

  if (decimalPart > 0) {
    words += ' ரூபாய் ' + convertLessThan100(decimalPart) + ' பைசா மட்டும்';
  } else {
    words += ' ரூபாய் மட்டும்';
  }

  if (isNegative) {
    words = 'கழித்தல் ' + words;
  }

  return words;
}
